// disabling no-target-blank eslint rule for this file
// so that we can add a link to the Cygnis website
// without needing to do rel="noreferrer"
/* eslint-disable react/jsx-no-target-blank */

// Next js Components
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'

import { Card, CardBody } from '@windmill/react-ui'

// Custom Components
import Testimonial from '../components/Testimonial'
import RequestConsultation from '../components/RequestConsultation'

const metaTitle =
    'AI & Machine Learning Development Company | ' +
    process.env.NEXT_PUBLIC_APP_NAME

const metaDescription =
    'We are a team of engineers, data scientists, and analysts with 10+ years experience helping businesses leverage technology to make smart data-driven decisions.'

const metaImage =
    process.env.NEXT_PUBLIC_APP_URL + '/og-images/og-images_company.jpg'

// What we can do

const whatWeCanDo = [
    {
        id: 1,
        title: 'Predictive Analysis',
        description:
            'We implement Machine Learning and Deep Learning models to uncover patterns in the underlying data that help guide the business make data-driven decisions.',
        img: '/about/predictive-analysis.svg',
    },
    {
        id: 2,
        title: 'Natural Language Processing',
        description:
            "We use Natural Language Processing to understand the meaning and context of your customer's messages to help you understand their needs and provide better service.",
        img: '/about/natural-language-processing.svg',
    },
    {
        id: 3,
        title: 'Computer Vision',
        description:
            'Computer Vision helps us understand and give context to the images and videos that are being captured, in order to provide accurate analysis and recommendations.',
        img: '/about/computer-vision.svg',
    },
]

const HowWeWorks = [
    {
        id: 1,
        title: 'Fully Managed Services',
        description:
            'We work closely with your team to ensure that your unique needs are met. Our data consultants will analyze your existing datasets and business goals to create the best solution for you.',
        img: '/about/fully-managed.svg',
    },
    {
        id: 2,
        title: 'Best-in-class AI & ML Platform',
        description:
            'Smart Answers is our best-in-class Artificial Intelligence (AI) & Machine Learning (ML) platform, built to be easy to use and easy to scale. No prior data science expertise required.',
        img: '/about/best-in-class.svg',
    },
    {
        id: 3,
        title: 'Intelligent Dashboards',
        description:
            'Smart Answers can feed your business dashboards with real-time information and insights to help you visualize your data in a way that is easy to understand and easy to use.',
        img: '/about/intelligent-apps.svg',
    },
]

const About = () => {
    return (
        <>
            <Head>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={metaImage} />
                <meta
                    property="og:url"
                    content={`${process.env.NEXT_PUBLIC_APP_URL}/company`}
                />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />
            </Head>
            <div>
                {/* Banner */}
                <div
                    className="banner about"
                    itemScope
                    itemType="https://schema.org/WebPage"
                >
                    <div className="inner">
                        <span className="tagline">ABOUT SMART ANSWERS</span>
                        <h1 itemProp="headline">
                            We Solve Real-World Problems using AI &amp; Machine
                            Learning!
                        </h1>
                        <h3 className="font-normal" itemProp="description">
                            We are a team of engineers, data scientists, and
                            analysts with 10+ years experience helping
                            businesses leverage technology to make smart
                            data-driven decisions.
                        </h3>
                        <span className="w-[42px] mx-auto mt-6 block">
                            <Image
                                src="/logo-icon-inverse.svg"
                                alt="ABOUT SMART ANSWERS"
                                layout="responsive"
                                width={42}
                                height={42}
                                itemProp="image"
                            />
                        </span>
                    </div>
                    <div className="bg-image">
                        <Image
                            src="/about/banner-bg.svg"
                            alt="ABOUT SMART ANSWERS"
                            layout="responsive"
                            width={924}
                            height={742}
                            itemProp="image"
                        />
                    </div>
                </div>
                <div className="container">
                    {/* =========== Section Founding Story =========== */}
                    <div
                        className="section"
                        itemScope
                        itemType="https://schema.org/WebPage"
                    >
                        <div className="flex ipad:flex-col">
                            <div>
                                <h2
                                    className="heading left"
                                    itemProp="headline"
                                >
                                    Founding Story
                                </h2>
                                <p
                                    className="text-xl text-common-black tablet:text-base"
                                    itemProp="description"
                                >
                                    Created by{' '}
                                    <a
                                        href="https://www.cygnismedia.com"
                                        target="_blank"
                                        className="hover:underline"
                                    >
                                        Cygnis Media
                                    </a>
                                    , a well established software development
                                    company in Silicon Valley since 2010, Smart
                                    Answers was built to fill a void in the
                                    marketplace where Machine Learning and AI
                                    solutions were either too expensive, or too
                                    intimidating for most.
                                </p>
                                <p
                                    className="text-xl text-common-black tablet:text-base"
                                    itemProp="description"
                                >
                                    <a
                                        href="https://www.forbes.com/sites/forbestechcouncil/2021/03/11/five-reasons-why-aiml-should-be-top-of-mind-in-2021"
                                        target="_blank"
                                        className="hover:underline"
                                    >
                                        AI &amp; ML will be the cornerstone of
                                        businesses
                                    </a>{' '}
                                    moving forward, regardless of industry or
                                    size. With this in mind, we built Smart
                                    Answers to provide an easy to use, scalable,
                                    lightning fast platform where anyone from
                                    business owners to data analysts can plug
                                    their data into our growing library of AI
                                    &amp; ML models and instantly gain
                                    actionable business intelligence without
                                    needing any prior expertise in data science.
                                </p>
                                <p
                                    className="text-xl text-common-black tablet:text-base"
                                    itemProp="description"
                                >
                                    To assist those that have very specific
                                    requirements, we offer consultancy services
                                    to help set up data pipelines, data
                                    cleansing, data extraction and custom AI
                                    &amp; ML model development to tailor the
                                    solution to their unique needs.
                                </p>
                            </div>
                            <div className="flex-shrink-0 pl-11 w-[400px] ipad:hidden">
                                <Image
                                    src="/about/founding-story.svg"
                                    alt="Founding Story"
                                    layout="responsive"
                                    width={346}
                                    height={446}
                                    itemProp="image"
                                />
                            </div>
                        </div>
                    </div>
                    {/* separator */}
                    <div className="separator"></div>

                    {/* =========== Section what we can do for you =========== */}
                    <div
                        className="section"
                        itemScope
                        itemType="https://schema.org/WebPage"
                    >
                        <h2 className="heading" itemProp="headline">
                            What We Can Do for You
                        </h2>
                        <p className="description" itemProp="description">
                            Most companies are sitting on a plethora of data
                            which is not being utilized. We help companies
                            extract value from this data. For those who need
                            help gathering relevant data, we help setup data
                            pipelines to capture the right datasets that will
                            help the business gain a competitive advantage in
                            their respective market.
                        </p>
                        <div>
                            <div className="grid grid-cols-3 gap-8 ipad:grid-cols-1 tablet:gap-4">
                                {whatWeCanDo.map((data) => (
                                    <Card className="base-card" key={data.id}>
                                        <CardBody className="text-center card-body tablet:text-left">
                                            <span className="block w-32 mx-auto image tablet:w-14">
                                                <Image
                                                    src={data.img}
                                                    layout="responsive"
                                                    alt="predictive-analysis"
                                                    width={128}
                                                    height={128}
                                                    itemProp="image"
                                                />
                                            </span>
                                            <div>
                                                <h3
                                                    className="title"
                                                    itemProp="headline"
                                                >
                                                    {data.title}
                                                </h3>
                                                <p
                                                    className="detail-content"
                                                    itemProp="description"
                                                >
                                                    {data.description}
                                                </p>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))}
                            </div>
                            <div className="text-center">
                                <p
                                    className="mt-8 mb-0 text-xl font-bold"
                                    itemProp="headline"
                                >
                                    Looking for a Machine Learning solution
                                    custom built for your needs?
                                    <Link href="/contact">
                                        <a className="pl-1 text-primary-500">
                                            Contact us
                                        </a>
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* separator */}
                    <div className="separator"></div>
                    <div className="section tablet:pt-6">
                        {/* Section How we work */}
                        <div
                            className="container-sm"
                            itemScope
                            itemType="https://schema.org/WebPage"
                        >
                            <Card className="text-center rounded-xl ipad:shadow-none p-10 ipad:p-0 bg-primary-500 ipad:bg-transparent h-[316px] lg:h-[280px] ipad:h-auto">
                                <CardBody>
                                    <h2
                                        className="text-3xl text-common-white ipad:text-primary-900"
                                        itemProp="headline"
                                    >
                                        How We Work
                                    </h2>
                                    <p
                                        className="text-base text-common-white max-w-[958px] mx-auto text-center mb-11 font-normal ipad:text-interface-900 ipad:mb-0"
                                        itemProp="description"
                                    >
                                        You can help yourself to our growing
                                        library of available AI &amp; ML models
                                        to get started instantly. Our team of
                                        experts are also available to work
                                        closely with you to provide consultancy
                                        on your specific needs and work with you
                                        to build custom models as needed.
                                    </p>
                                </CardBody>
                            </Card>
                        </div>
                        <div className="relative grid grid-cols-3 gap-8 -mt-24 ipad:gap-4 ipad:mt-4 ipad:grid-cols-1 tablet:gap-4">
                            {/* Fully Managed Services */}
                            {HowWeWorks.map((data) => (
                                <Card className="base-card dark" key={data.id}>
                                    <CardBody className="text-center card-body tablet:text-left">
                                        <span className="block w-16 mx-auto image tablet:w-14">
                                            <Image
                                                src={data.img}
                                                alt="Fully Managed Services"
                                                layout="responsive"
                                                width={128}
                                                height={128}
                                                itemProp="image"
                                            />
                                        </span>
                                        <div>
                                            <h3 className="title">
                                                {data.title}
                                            </h3>
                                            <p
                                                className="detail-content"
                                                itemProp="description"
                                            >
                                                {data.description}
                                            </p>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    </div>
                    {/* separator */}
                    <div className="separator"></div>
                    {/* =========== Testimonial =========== */}
                    <div className="pb-16 section tablet:pb-6">
                        <Testimonial />
                    </div>
                    {/* =========== Request a consultation =========== */}
                    <div className="pt-0 section">
                        <RequestConsultation />
                    </div>
                </div>
            </div>
        </>
    )
}
export default About
